import React, { useEffect, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getRequest, postRequest, putRequest, deleteRequest } from 'api';
import { toast } from 'react-toastify';

interface RevenueBlock {
    id: number;
    schoolId: number;
    code: string;
    name: string;
    sortOrder: number;
    status: number;
    createdDate?: string;
}

const RevenueBlockList: React.FC = () => {
    const [data, setData] = useState<RevenueBlock[]>([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<number | undefined>(undefined);

    const [modalVisible, setModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState<RevenueBlock | null>(null);

    const [form] = Form.useForm();
    const [editForm] = Form.useForm();

    const fetchData = async () => {
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId) {
            toast.error('Không tìm thấy schoolId');
            return;
        }

        setLoading(true);
        try {
            let url = `/cms/accountant/revenue-blocks?schoolId=${schoolId}`;
            if (statusFilter !== undefined) {
                url += `&status=${statusFilter}`;
            }
            const res = await getRequest(url);
            if (res?.code === 200 && Array.isArray(res.info)) {
                setData(res.info);
            } else {
                toast.error(res?.msg || 'Không thể tải danh sách khối thu');
            }
        } catch (error) {
            toast.error('Lỗi khi tải danh sách khối thu');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [statusFilter]);

    const handleCreate = async (values: any) => {
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId) return;

        try {
            const res = await postRequest(`/cms/accountant/revenue-blocks?schoolId=${schoolId}`, {
                code: values.code,
                name: values.name,
                sortOrder: values.sortOrder || 0,
                status: 1
            });

            if (res?.code === 200) {
                toast.success('Tạo khối thu thành công');
                setModalVisible(false);
                form.resetFields();
                fetchData();
            } else {
                toast.error(res?.msg || 'Tạo khối thu thất bại');
            }
        } catch (err: any) {
            toast.error(err?.message || 'Lỗi khi tạo khối thu');
        }
    };

    const handleEdit = async (values: any) => {
        if (!selectedItem) return;
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId) return;

        try {
            const res = await putRequest(`/cms/accountant/revenue-blocks/${selectedItem.id}?schoolId=${schoolId}`, {
                id: selectedItem.id,
                code: values.code,
                name: values.name,
                sortOrder: values.sortOrder || 0,
                status: values.status !== undefined ? values.status : 1
            });

            if (res?.code === 200) {
                toast.success('Cập nhật khối thu thành công');
                setEditModalVisible(false);
                setSelectedItem(null);
                fetchData();
            } else {
                toast.error(res?.msg || 'Cập nhật thất bại');
            }
        } catch (err: any) {
            toast.error(err?.message || 'Lỗi khi cập nhật');
        }
    };

    const handleDelete = async (id: number) => {
        const schoolId = localStorage.getItem('schoolId');
        if (!schoolId) return;

        try {
            const res = await deleteRequest(`/cms/accountant/revenue-blocks/${id}?schoolId=${schoolId}`);
            if (res?.code === 200) {
                toast.success('Xóa khối thu thành công');
                fetchData();
            } else {
                toast.error(res?.msg || 'Xóa khối thu thất bại');
            }
        } catch (err: any) {
            toast.error(err?.message || 'Lỗi khi xóa khối thu');
        }
    };

    const columns = [
        {
            title: 'STT',
            key: 'index',
            width: 70,
            align: 'center' as const,
            render: (_: any, __: any, index: number) => index + 1,
        },
        {
            title: 'Mã khối thu',
            dataIndex: 'code',
            key: 'code',
            render: (code: string) => <Tag color="blue" style={{ fontWeight: 600 }}>{code}</Tag>,
        },
        {
            title: 'Tên khối thu',
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => <span style={{ fontWeight: 500 }}>{name}</span>,
        },
        {
            title: 'Thứ tự hiển thị',
            dataIndex: 'sortOrder',
            key: 'sortOrder',
            align: 'center' as const,
            width: 130,
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            align: 'center' as const,
            width: 140,
            render: (status: number) => (
                <Tag color={status === 1 ? 'green' : 'default'}>
                    {status === 1 ? 'Hoạt động' : 'Ngừng áp dụng'}
                </Tag>
            ),
        },
        {
            title: 'Hành động',
            key: 'action',
            align: 'center' as const,
            width: 180,
            render: (_: any, record: RevenueBlock) => (
                <Space size="middle">
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setSelectedItem(record);
                            editForm.setFieldsValue({
                                code: record.code,
                                name: record.name,
                                sortOrder: record.sortOrder,
                                status: record.status
                            });
                            setEditModalVisible(true);
                        }}
                    >
                        Sửa
                    </Button>
                    <Popconfirm
                        title="Xác nhận xóa khối thu"
                        description="Bạn có chắc chắn muốn xóa khối thu này không?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Xóa"
                        cancelText="Hủy"
                        okButtonProps={{ danger: true }}
                    >
                        <Button type="link" danger icon={<DeleteOutlined />}>
                            Xóa
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: '4px 0' }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            form.resetFields();
                            form.setFieldsValue({ sortOrder: 0 });
                            setModalVisible(true);
                        }}
                    >
                        Thêm khối thu
                    </Button>
                    <Select
                        placeholder="Lọc theo trạng thái"
                        value={statusFilter}
                        onChange={(val) => setStatusFilter(val)}
                        allowClear
                        style={{ width: 180 }}
                    >
                        <Select.Option value={1}>Hoạt động</Select.Option>
                        <Select.Option value={0}>Ngừng áp dụng</Select.Option>
                    </Select>
                </Space>
                <div style={{ color: '#888', fontSize: 13 }}>
                    * Khối thu dùng để phân loại học sinh phục vụ Đăng ký nhanh nguồn thu theo tháng
                </div>
            </div>

            <Table
                rowKey="id"
                dataSource={data}
                columns={columns}
                loading={loading}
                pagination={false}
                bordered
            />

            {/* Modal Thêm mới */}
            <Modal
                title="Thêm khối thu mới"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={() => form.submit()}
                okText="Tạo khối thu"
                cancelText="Hủy"
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleCreate}>
                    <Form.Item
                        label="Mã khối thu"
                        name="code"
                        rules={[
                            { required: true, message: 'Vui lòng nhập mã khối thu!' },
                            { max: 50, message: 'Mã khối thu tối đa 50 ký tự' }
                        ]}
                    >
                        <Input placeholder="Ví dụ: BAN_TRU, NGOAI_TRU, TAM_THOI..." style={{ textTransform: 'uppercase' }} />
                    </Form.Item>
                    <Form.Item
                        label="Tên khối thu"
                        name="name"
                        rules={[
                            { required: true, message: 'Vui lòng nhập tên khối thu!' },
                            { max: 100, message: 'Tên khối thu tối đa 100 ký tự' }
                        ]}
                    >
                        <Input placeholder="Ví dụ: Khối Bán trú, Khối Ngoại trú..." />
                    </Form.Item>
                    <Form.Item
                        label="Thứ tự hiển thị"
                        name="sortOrder"
                        initialValue={0}
                    >
                        <InputNumber min={0} max={999} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal Chỉnh sửa */}
            <Modal
                title="Chỉnh sửa khối thu"
                open={editModalVisible}
                onCancel={() => {
                    setEditModalVisible(false);
                    setSelectedItem(null);
                }}
                onOk={() => editForm.submit()}
                okText="Lưu thay đổi"
                cancelText="Hủy"
                destroyOnClose
            >
                <Form form={editForm} layout="vertical" onFinish={handleEdit}>
                    <Form.Item
                        label="Mã khối thu"
                        name="code"
                        rules={[
                            { required: true, message: 'Vui lòng nhập mã khối thu!' },
                            { max: 50, message: 'Mã khối thu tối đa 50 ký tự' }
                        ]}
                    >
                        <Input placeholder="Ví dụ: BAN_TRU, NGOAI_TRU..." style={{ textTransform: 'uppercase' }} />
                    </Form.Item>
                    <Form.Item
                        label="Tên khối thu"
                        name="name"
                        rules={[
                            { required: true, message: 'Vui lòng nhập tên khối thu!' },
                            { max: 100, message: 'Tên khối thu tối đa 100 ký tự' }
                        ]}
                    >
                        <Input placeholder="Ví dụ: Khối Bán trú..." />
                    </Form.Item>
                    <Form.Item
                        label="Thứ tự hiển thị"
                        name="sortOrder"
                    >
                        <InputNumber min={0} max={999} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        label="Trạng thái"
                        name="status"
                    >
                        <Select>
                            <Select.Option value={1}>Hoạt động</Select.Option>
                            <Select.Option value={0}>Ngừng áp dụng</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default RevenueBlockList;
